<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Input } from '@sveltestrap/sveltestrap';

  import { DatePicker } from '@client/components/datepicker';
  import { FolderTreeDropdown } from '@client/components/folder-tree-dropdown';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';
  import { DomainService } from '@client/services/domain';
  import { TargetingService } from '@client/services/targeting';

  import { structureTree } from '@client/utils/folder-tree';
  import { sanitizeInputValue, sanitizeTagValue } from '@client/utils/sanitize';

  import { CAMPAIGN_CATEGORIES_ARRAY } from '@client/enums';

  import { formatDate } from '@common/utils/dates';

  export let tagFilters = [];
  export let rulename = '';
  export let enableSchedule = false;
  export let selectedTags = [];
  export let selectedCategories = [];
  export let startDate = '';
  export let endDate = '';
  export let selectedFolders = [];
  export let invalidRulename = false;

  let currentRulenames = [];
  let tags = [];
  let filterText = '';
  let categories = [...CAMPAIGN_CATEGORIES_ARRAY];

  let dateFormat = 'EEE, MMM d, yy @ h:mm a';
  let isOpen = false;
  let defaultYear = new Date().getFullYear();
  let defaultMonth = new Date().getMonth();

  let formattedStartDate = '';
  let formattedEndDate = '';

  let folders = [];
  let selectedLabels = [];
  let defaultTags = [];

  const getCurrentRulenames = (data) => {
    data.forEach((campaign) => {
      currentRulenames.push(campaign.rulename);
    });

    return currentRulenames;
  };

  const toggleDatePicker = () => {
    return startDate && !endDate ? (isOpen = true) : (isOpen = !isOpen);
  };

  const formatDateString = (dateString) => {
    if (!dateString) {
      return '';
    }

    return formatDate(new Date(dateString), dateFormat);
  };

  const onTagChange = (e) => {
    const currentTags = tags || [];

    filterText = sanitizeTagValue(filterText);

    if (!selectedTags) {
      selectedTags = [];
      return;
    }

    if (e.key === 'Enter' || e.type === 'blur') {
      if (selectedTags.map(({ value }) => value).includes(filterText)) {
        filterText = '';
        return;
      }
      if (!currentTags.includes(filterText) && filterText.length) {
        selectedTags = [{ label: filterText, value: filterText }, ...selectedTags];
        filterText = '';
      }
    } else {
      if (!filterText.length && e.detail.value) {
        const selected = e.detail.value;
        if (!currentTags.includes(selected) && selected.length) {
          selectedTags = [selected, ...selectedTags];
        }
      }
    }
  };

  const onSelectCategory = () => {
    selectedCategories = selectedCategories.map((category) => {
      return { ...category, label: $format(category.label), checked: true };
    });
  };

  const createTree = async () => {
    const response = await AssetsService.getAssetFolders();

    if (!response.length) {
      folders = [];
      return;
    }

    folders = [structureTree(response)];
  };

  const resetFolderSelections = () => {
    selectedFolders = [];
    selectedLabels = [];
  };

  const updateFolderSelections = (id, name, override) => {
    const exists = override ?? selectedFolders.includes(id);

    if (exists) {
      if (selectedFolders.includes(id)) {
        selectedFolders.splice(selectedFolders.indexOf(id), 1);
        selectedLabels.splice(selectedLabels.indexOf(name), 1);
      }
    } else {
      resetFolderSelections();

      if (!selectedFolders.includes(id)) {
        selectedFolders.push(id);
        selectedLabels.push(name);
      }
    }

    selectedFolders = [...selectedFolders];
    selectedLabels = [...selectedLabels];
  };

  const onFolderSelection = ({ id, name }, override) => {
    updateFolderSelections(id, name, override);
  };

  const onSelect = (e, file) => {
    e.preventDefault();
    e.stopPropagation();

    onFolderSelection(file);
  };

  onMount(async () => {
    createTree();

    const campaigns = await CampaignsService.getAll();

    getCurrentRulenames(campaigns);

    const settings = await DomainService.getSettings();
    const targeting = await TargetingService.get();

    const targertingTags = targeting?.departments.map(({ tags }) => tags.map(({ text }) => text)).flat() || [];

    const targetingTagsSet = targertingTags.map((tag) => ({
      label: tag,
      value: tag
    }));

    defaultTags = (settings.adtags || []).map((tag) => ({
      label: tag,
      value: tag
    }));

    defaultTags = [...defaultTags, ...targetingTagsSet];
  });

  $: formattedStartDate = formatDateString(startDate);
  $: formattedEndDate = formatDateString(endDate);
  $: invalidRulename = currentRulenames.includes(rulename);
  $: tagFiltersArray = Object.values(tagFilters).map(({ label, value }) => ({ label, value }));
  $: tags = (
    tagFiltersArray.length
      ? [...tagFiltersArray, ...defaultTags.filter(({ label }) => !tagFiltersArray.includes(label))]
      : defaultTags || []
  )
    .reduce((acc, tag) => {
      if (!acc.some(({ label }) => label === tag.label)) {
        acc.push(tag);
      }
      return acc;
    }, [])
    .sort((a, b) => a?.label.localeCompare(b?.label));
  $: categories = categories.map((category) => {
    return { ...category, label: $format(category.label) };
  });

  $: if (folders.length && selectedFolders.length && !selectedLabels.length) {
    selectedLabels = selectedFolders.map((id) => {
      const folder = folders.flatMap((f) => f.files).find((file) => file.id === id);
      return folder ? folder.name : '';
    });
  }
</script>

<div class="section">
  <div class="campaign-details">
    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.NAME')}</b>
        <p>{$format('label.DESCRIPTION.NAME_CAMPAIGN')}</p>
      </div>
      <div class="campaign-actions name">
        <Input
          type="text"
          placeholder={$format('label.PLACEHOLDER.INPUT', {
            values: { param: $format('label.NAME').toLowerCase() }
          })}
          invalid={invalidRulename}
          feedback={$format('label.VALIDATION_NAME_EXISTS', {
            values: { type: $format('label.CAMPAIGN'), param: $format('label.NAME').toLowerCase() }
          })}
          on:keyup={() => {
            rulename = sanitizeInputValue(rulename);
          }}
          bind:value={rulename}
        />
      </div>
    </div>
    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.SCHEDULE')}</b>
        <p>{$format('label.DESCRIPTION.SCHEDULE_CAMPAIGN')}</p>
      </div>
      <div class="campaign-actions">
        <Switch bind:checked={enableSchedule} />
      </div>
    </div>

    {#if enableSchedule}
      <div class="campaign-item">
        <div class="campaign-info">
          <b>{$format('label.SCHEDULE_DATES')}</b>
          <p>{$format('label.DESCRIPTION.SCHEDULE_DATES')}</p>
        </div>
        <div class="campaign-actions date">
          <Input
            type="text"
            on:click={toggleDatePicker}
            class="dates"
            value="{formattedStartDate} - {formattedEndDate}"
          />
          <DatePicker
            align="right"
            isRange
            showTimePicker
            enableFutureDates
            isMultipane
            showPresets={false}
            bind:startDate
            bind:isOpen
            bind:defaultMonth
            bind:defaultYear
            bind:endDate
          />
        </div>
      </div>
    {/if}

    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.TAGS')}</b>
        <p>{$format('label.DESCRIPTION.TAGS_CAMPAIGN')}</p>
      </div>
      <div class="campaign-actions" on:keyup={onTagChange} role="button" tabindex="0">
        <Select
          bind:items={tags}
          bind:filterText
          bind:value={selectedTags}
          on:change={onTagChange}
          on:blur={onTagChange}
          placeholder={$format('label.PLACEHOLDER.SELECT_OR_CREATE_NEW_TAG')}
          clearable={false}
          multiple
          showChevron
          searchable
        >
          <slot name="empty" slot="empty">
            <div class="empty">
              {#if !filterText.length}
                {$format('label.START_TYPING_TO_FIND_TAGS')}
              {:else if selectedTags?.map(({ value }) => value).includes(filterText)}
                {$format('label.TAG_ALREADY_EXISTS')}
              {:else}
                {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: filterText } })}
              {/if}
            </div>
          </slot>
        </Select>
      </div>
    </div>
    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.CATEGORIES')}</b>
        <p>{$format('label.DESCRIPTION.CATEGORIES_CAMPAIGN')}</p>
      </div>
      <div class="campaign-actions">
        <Select
          bind:items={categories}
          bind:value={selectedCategories}
          on:change={onSelectCategory}
          placeholder={$format('label.PLACEHOLDER.SELECT_CATEGORY')}
          multiple
          showChevron
          searchable
          clearable={false}
        />
      </div>
    </div>

    {#if folders.length}
      <div id="folder-section-campaigns-modal" class="campaign-item">
        <div class="campaign-info">
          <b>{$format('label.ASSET_FOLDER')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_FOLDER')}</p>
        </div>
        <div class="campaign-actions">
          <FolderTreeDropdown
            bind:folders
            selectable
            selected={selectedFolders}
            bind:selectedLabels
            {onSelect}
            expanded={false}
            direction="right"
          >
            {@html selectedFolders.length
              ? `<i class="os-icon-folder"></i> ${selectedLabels[0]}`
              : $format('label.CHOOSE_FOLDER')}
          </FolderTreeDropdown>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .campaign-actions {
    &.name {
      flex-direction: column;
      justify-content: center;
    }

    &.date {
      display: block;
      width: 275px;
    }

    .empty {
      color: var(--color-gray-600);
      padding: var(--padding-xlarge);
      text-align: center;
    }
  }
</style>
